import { Injectable } from "@angular/core";
import { ApiUrlService } from "./api-url.service";
import { CacheService } from "./cache.service";
import { NetworkService } from "./network.service";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { catchError, EMPTY, map, of, take, tap } from "rxjs";
import { lastValueFrom } from "rxjs";
import { Capacitor } from "@capacitor/core";

@Injectable({
  providedIn: "root",
})
export class FeatureService {
  private apiUrl = `${this.apiUrlService.getApiUrl()}/api/features`;
  private apiUrlOrder = `${this.apiUrlService.getApiUrl()}/api/features/order`;
  private featureDescriptions: any[] = [];
  private readonly isNative = Capacitor.getPlatform() !== "web";
  private readonly featureCacheKey = "features-list"; // <‑‑ single cached object key

  constructor(
    private http: HttpClient,
    private networkService: NetworkService,
    private apiUrlService: ApiUrlService,
    private cacheService: CacheService
  ) {}

  /**
   * Retrieve available features with a two‑tier strategy:
   *
   * ‣ **Capacitor**
   *    1. Attempt to load the JSON array from IndexedDB/Cache (via CacheService).
   *    2. If present ⇒ return immediately **and** refresh the cache in the background.
   *    3. If absent ⇒ fetch from API, then write the response to cache.
   *
   * ‣ **Web (PWA / browser)** – always pull fresh from API (legacy behaviour).
   */
  public async getFeaturesWithTimeout(): Promise<any[] | null> {
    console.log("FeatureService getFeaturesWithTimeout");
    const cacheKey = this.featureCacheKey;

    /* ───────────────────────── Capacitor branch ─────────────────────── */
    if (this.isNative) {
      try {
        const cached = await this.cacheService.getCachedData(cacheKey);
        if (cached) {
          console.log("FeatureService returning cached feature list");

          // Fire‑and‑forget background refresh
          this.refreshFeaturesInBackground(cacheKey);
          return cached;
        }
      } catch (e) {
        console.warn(
          "FeatureService cache lookup failed – proceeding to network",
          e
        );
      }
    }

    /* ───────────────────────── Network fetch (also web default) ─────── */
    try {
      const fresh = await this.http
        .get<any[]>(this.apiUrl, {
          headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
          params: new HttpParams(),
        })
        .toPromise();

      // Persist to cache for native use‑cases
      if (this.isNative && fresh) {
        try {
          await this.cacheService.setCachedData(cacheKey, fresh);
        } catch (e) {
          console.warn(
            "FeatureService unable to write feature list to cache",
            e
          );
        }
      }

      return fresh;
    } catch (error: any) {
      console.error("FeatureService error fetching features:", error);
      if (this.networkService.isNetworkError(error)) {
        this.networkService.markInternetDown();
      }
      return null;
    }
  }

  /** Background updater (native only) */
  private refreshFeaturesInBackground(cacheKey: string): void {
    console.log("FeatureService refreshing features in background");
    this.http
      .get<any[]>(this.apiUrl, {
        headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
        params: new HttpParams(),
      })
      .toPromise()
      .then(async (fresh) => {
        if (fresh) {
          await this.cacheService.setCachedData(cacheKey, fresh);
          console.log("FeatureService feature list cache refreshed");
        }
      })
      .catch((err) =>
        console.warn("FeatureService background feature refresh failed", err)
      );
  }

  /* ───────────────────────── rest of class unchanged ────────────────── */

  // Main entry‑point: download (or load from cache) the description JSONs
  public async getFeatureDescriptions(): Promise<any[]> {
    const features = await this.getFeaturesWithTimeout();

    // Prepare & start background fetches
    let fetchPromises: Promise<any>[] = [];
    if (features?.length) {
      fetchPromises = features.map((feature) => {
        const featureUrl = feature.feature_url
          ? `${feature.feature_url}feature-description.json`
          : `${feature.baseUrl}feature-description.json`;

        return lastValueFrom(
          this.cacheService.getResource(featureUrl, true).pipe(
            tap((description) => {
              const shouldCache = description?.data?.offline === "true";
              if (this.isNative || shouldCache) {
                this.cacheFeatureResources(description.data);
              }
            })
          )
        ).catch((err) => {
          console.error(`Failed to refresh ${featureUrl}`, err);
        });
      });
    }

    // Try to load whatever is already cached
    let cached = await this.loadCachedFeatureDescriptions();

    console.log("Features:", features);
    // TOOD print each cached feature description
    cached.forEach((f) => {
      console.log("Cached feature description:", f);
    });

    // If nothing was cached, wait for the background fetch to finish, then reload
    // or if we are partially cached (ie race condition)
    console.log("Cached feature descriptions:", cached.length);
    console.log("Features:", features.length);
    //|| cached.length < features.length doesn't seem to be working
    if (!cached.length || cached.length < features.length) {
      console.log("Waiting for background fetch to finish");
      await Promise.all(fetchPromises);
      cached = await this.loadCachedFeatureDescriptions();
    } else {
      console.log("Using cached feature descriptions");
      // Fire‐and‐forget
      void Promise.all(fetchPromises);
    }

    // Merge in feature_id and return
    const withFeatureId = cached.map((fdata) => {
      const featureId = features.find(
        (feat) => feat.feature_url.trim() === fdata.baseUrl.trim()
      )?.feature_id;
      return { ...fdata, feature_id: featureId };
    });
    this.featureDescriptions = withFeatureId;
    return withFeatureId;
  }

  // Cache the auxiliary resources for a given feature
  private async cacheFeatureResources(feature: any): Promise<void> {
    console.log("FeatureService caching resources for feature:", feature);
    const baseUrl = feature.baseUrl || feature.feature_url;
    const resourcesToCache = [
      "feature-description.json",
      "index.html",
      "bundle.js",
      feature.homeIcon,
      ...(feature.footerIcons?.flatMap((icon: any) => [
        icon.iconSrc,
        icon.negIconSrc,
      ]) || []),
    ];

    for (const resource of resourcesToCache) {
      const fullUrl = `${baseUrl}${resource}`;
      try {
        await lastValueFrom(
          this.cacheService.getResourceWithoutRefresh(fullUrl, true)
        );
      } catch (err) {
        console.error("Error caching resource:", fullUrl, err);
      }
    }
  }

  // Retrieve any cached feature‑descriptions from storage
  private async loadCachedFeatureDescriptions(): Promise<any[]> {
    console.log("FeatureService loadCachedFeatureDescriptions");
    const cachedDescriptions = (
      await this.cacheService.getCachedItems((key) =>
        key.endsWith("feature-description.json")
      )
    ).map((item) => {
      console.log("FeatureService loadCachedFeatureDescriptions item:", item);
      return item.data;
    });
    return cachedDescriptions;
  }

  /* ───────────────────────── helper look‑ups (unchanged) ────────────── */

  public getBaseUrlByBasePath(basePath: string): Promise<any> {
    return this.featureDescriptions.length === 0
      ? this.getFeatureDescriptions().then(() =>
          this.findFeatureUrlByPath(basePath)
        )
      : Promise.resolve(this.findFeatureUrlByPath(basePath));
  }

  private findFeatureUrlByPath(basePath: string) {
    return this.featureDescriptions.find(
      (f) => f.defaultPath.replace(/\//g, "") === basePath
    )?.feature_url;
  }

  public async getFeatureDescriptionByBasePath(basePath: string): Promise<any> {
    console.log("React Native getFeatureDescriptionByBasePath");
    console.log("React Native:", await this.getFeatureDescriptions());

    return (await this.getFeatureDescriptions()).find(
      (f) => f.defaultPath.replace(/\//g, "") === basePath
    );
  }

  public async getFeatureDescriptionByBaseUrl(baseUrl: string): Promise<any> {
    if (this.featureDescriptions.length === 0)
      await this.getFeatureDescriptions();
    return this.featureDescriptions.find(
      (f) => (f.feature_url ?? f.baseUrl) === baseUrl
    );
  }

  public async getFeatureOrder(orgId: string): Promise<any | null> {
    console.log("FeatureService getFeatureOrder: ", orgId);
    const cacheKey = `featureOrder:${orgId}`;
    try {
      let cachedData = await this.cacheService.getCachedData(cacheKey);
      if (cachedData) {
        try {
          console.log(
            "FeatureService getFeatureOrder: cache hit: ",
            cachedData
          );
          const order = cachedData;
          console.log(
            "FeatureService getFeatureOrder: parsed cache hit: ",
            order
          );

          // ── fire-and-forget background refresh ──────────────────────────
          this.http
            .get<any[]>(`${this.apiUrlOrder}/${orgId}`, {
              headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
              params: new HttpParams(),
            })
            .pipe(
              take(1), // single hit
              tap((fresh) => this.cacheService.setCachedData(cacheKey, fresh)),
              catchError((err) => {
                console.error(
                  `FeatureService background refresh failed for org ${orgId}:`,
                  err
                );
                return EMPTY; // swallow error
              })
            )
            .subscribe(); // no blocking
          // ────────────────────────────────────────────────────────────────

          return order;
        } catch (err) {
          console.error(
            `FeatureService error parsing cached order for org ${orgId}:`,
            err
          );
          // await this.cacheService.removeItem(cacheKey);
          return await this.fetchAndCacheFeatureOrder(cacheKey, orgId);
        }
      } else {
        return await this.fetchAndCacheFeatureOrder(cacheKey, orgId);
      }
    } catch (err) {
      console.error("FeatureService error accessing cache:", err);
      return await this.fetchAndCacheFeatureOrder(cacheKey, orgId);
    }
  }

  private async fetchAndCacheFeatureOrder(
    cacheKey: string,
    orgId: string
  ): Promise<any[]> {
    console.log("FeatureService fetchAndCacheFeatureOrder: ", cacheKey, orgId);

    // 1) Try to read from cache
    const cached = this.cacheService.getCachedData(cacheKey);
    if (cached != null) {
      // Kick off a non-blocking refresh
      lastValueFrom(
        this.http.get<any[]>(`${this.apiUrlOrder}/${orgId}`, {
          headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
          params: new HttpParams(),
        })
      )
        .then((orderData) => {
          this.cacheService.setCachedData(cacheKey, orderData);
        })
        .catch((err) => {
          console.error("Background refresh failed:", err);
          if (this.networkService.isNetworkError(err)) {
            this.networkService.markInternetDown();
          }
        });

      // Immediately return stale (or fresh) cache
      return cached;
    }

    // 2) No cache yet — block until we get data (or empty fallback)
    try {
      const orderData = await lastValueFrom(
        this.http.get<any[]>(`${this.apiUrlOrder}/${orgId}`, {
          headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
          params: new HttpParams(),
        })
      );
      // Cache it for next time
      this.cacheService.setCachedData(cacheKey, orderData);
      return orderData;
    } catch (err: any) {
      console.error("FeatureService fetching feature order:", err);

      // Ensure cache still gets something (empty array)
      await this.cacheService.setCachedData(cacheKey, []);
      if (this.networkService.isNetworkError(err)) {
        this.networkService.markInternetDown();
      }
      return [];
    }
  }
}
